import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import SuperAdminService from '../../service/super-admin.service';
import { useNotification } from '../../contexts/NotificationContext';

const PROVIDERS = [
  { value: 'DEEPSEEK', label: 'DeepSeek' },
  { value: 'OPENAI', label: 'OpenAI (GPT)' },
];

function SuperAdminMirandaPage() {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    provider: 'DEEPSEEK',
    modelId: '',
    label: '',
    active: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await SuperAdminService.listAiModels(false);
      setModels(Array.isArray(data) ? data : []);
    } catch (err) {
      notify('tr', 'danger', 'Erro ao carregar modelos', 'icon-alert-circle-exc');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = { DEEPSEEK: [], OPENAI: [] };
    models.forEach((m) => {
      if (!map[m.provider]) map[m.provider] = [];
      map[m.provider].push(m);
    });
    return map;
  }, [models]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await SuperAdminService.createAiModel({
        provider: form.provider,
        modelId: form.modelId.trim(),
        label: form.label.trim(),
        active: form.active,
      });
      notify('tr', 'success', 'Modelo cadastrado', 'icon-check-2');
      setCreateOpen(false);
      setForm({ provider: 'DEEPSEEK', modelId: '', label: '', active: true });
      load();
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao cadastrar modelo',
        'icon-alert-circle-exc',
      );
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (model) => {
    try {
      await SuperAdminService.updateAiModel(model.id, { active: !model.active });
      notify(
        'tr',
        'success',
        model.active ? 'Modelo desativado' : 'Modelo ativado',
        'icon-check-2',
      );
      load();
    } catch {
      notify('tr', 'danger', 'Erro ao atualizar modelo', 'icon-alert-circle-exc');
    }
  };

  return (
    <div className="content">
      <Row className="mb-4 align-items-center">
        <Col md="8">
          <h2 className="mb-1">Miranda AI — Modelos</h2>
          <p className="text-muted mb-0">
            Catálogo de provedores e modelos disponíveis para as contas
          </p>
        </Col>
        <Col md="4" className="text-md-right mt-3 mt-md-0">
          <Button color="secondary" outline className="mr-2" onClick={() => navigate('/superadmin')}>
            Contas
          </Button>
          <Button color="primary" onClick={() => setCreateOpen(true)}>
            Novo modelo
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        PROVIDERS.map((prov) => (
          <Card key={prov.value} className="mb-4">
            <CardHeader>
              <h4 className="card-title mb-0">{prov.label}</h4>
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Model ID</th>
                    <th>Status</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {(grouped[prov.value] || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-muted text-center">
                        Nenhum modelo cadastrado
                      </td>
                    </tr>
                  ) : (
                    (grouped[prov.value] || []).map((m) => (
                      <tr key={m.id}>
                        <td>{m.label}</td>
                        <td>
                          <code>{m.modelId}</code>
                        </td>
                        <td>
                          <Badge color={m.active ? 'success' : 'secondary'}>
                            {m.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td className="text-right">
                          <Button
                            size="sm"
                            color={m.active ? 'warning' : 'success'}
                            outline
                            onClick={() => toggleActive(m)}
                          >
                            {m.active ? 'Desativar' : 'Ativar'}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        ))
      )}

      <Modal isOpen={createOpen} toggle={() => setCreateOpen(false)} className="super-admin-dialog">
        <ModalHeader toggle={() => setCreateOpen(false)}>Novo modelo</ModalHeader>
        <ModalBody>
          <Form id="super-admin-miranda-model-form" onSubmit={handleCreate}>
            <FormGroup>
              <Label>Provedor</Label>
              <Input
                type="select"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Model ID</Label>
              <Input
                required
                value={form.modelId}
                onChange={(e) => setForm({ ...form, modelId: e.target.value })}
                placeholder="ex: deepseek-v4-flash ou gpt-4o-mini"
              />
            </FormGroup>
            <FormGroup>
              <Label>Label</Label>
              <Input
                required
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Nome amigável"
              />
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />{' '}
                Ativo
              </Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="super-admin-modal-footer">
          <Button color="secondary" outline type="button" onClick={() => setCreateOpen(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            form="super-admin-miranda-model-form"
            disabled={creating}
          >
            {creating ? 'Salvando...' : 'Cadastrar'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default SuperAdminMirandaPage;
